import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { CaseSteps, type CaseStep } from './ObjectionCase';

/**
 * Which information step to show before Adyen's onboarding (ClickUp 869fncme5):
 * `start` the first time, `capabilities` or `bankAccount` when the Hantverkare
 * completes what Adyen still needs.
 */
export type KycIntroVariant = 'start' | 'capabilities' | 'bankAccount';

export const KYC_INTRO_MINUTES = 10;
export const KYC_INTRO_OWNER_PERCENT = 25;

interface KycIntroModalProps {
  variant: KycIntroVariant;
  /** Runs today's start: start(), the mock handling, window.open and messages. */
  onContinue: () => void;
  onClose: () => void;
}

const STEP_KEYS = ['company', 'owners', 'idCheck', 'bankAccount'] as const;
/** The three blocks a phone shows one at a time. */
const MOBILE_BLOCKS = 3;

/**
 * The information step before the Hantverkare's KYC with Adyen: the account
 * already exists, why the check is needed, what to have at hand and that the
 * company signatory does their own steps. It always precedes the start and
 * stores nothing: "Gör det senare" closes it without starting.
 */
export function KycIntroModal({ variant, onContinue, onClose }: KycIntroModalProps) {
  const { t } = useTranslation();
  const [block, setBlock] = useState(0);
  const steps: CaseStep[] = STEP_KEYS.map(key => ({ label: t(`kycIntro.steps.${key}`) }));

  if (variant !== 'start') {
    // Companies that started get the short variant: where they stand and what is missing.
    const done = variant === 'bankAccount' ? 3 : 2;
    return (
      <Modal
        title={t(`kycIntro.short.${variant}.title`)}
        onClose={onClose}
        closeAriaLabel={t('common.close')}
        fullscreenBelow="sm"
        dialogClassName="objection-dialog"
        headerClassName="objection-header"
        renderHeader={titleId => (
          <div className="objection-header-main">
            <div className="objection-kicker">{t('kycIntro.short.kicker')}</div>
            <div className="objection-header-title">
              <h2 className="modal-title" id={titleId}>{t(`kycIntro.short.${variant}.title`)}</h2>
            </div>
          </div>
        )}
        footer={
          <div className="modal-action-row">
            <button type="button" className="btn btn-outline-primary" onClick={onClose}>
              {t('kycIntro.actions.later')}
            </button>
            <button type="button" className="btn btn-success" onClick={onContinue} data-testid="kyc-intro-continue">
              {t(`home.todoNow.items.${variant}.action`)}
            </button>
            <p className="modal-action-note text-explain mb-0">{t('kycIntro.short.kept')}</p>
          </div>
        }
      >
        <div className="d-flex flex-column gap-3" data-testid="kyc-intro" data-variant={variant}>
          <div>
            <p className="objection-kicker mb-2">{t('kycIntro.short.progress', { done, total: STEP_KEYS.length })}</p>
            <CaseSteps steps={steps} currentStep={done} />
          </div>
          <p className="mb-0">{t(`kycIntro.short.${variant}.text`)}</p>
          <div className="kyc-intro-note">
            <i className="feather-info" aria-hidden="true"></i>
            <span>{t(`kycIntro.short.${variant}.hint`)}</span>
          </div>
        </div>
      </Modal>
    );
  }

  // Below sm the blocks come one at a time (Bootstrap display classes only);
  // from sm everything shows at once.
  const blockClass = (index: number) => (index === block ? 'd-block' : 'd-none d-sm-block');
  const lastBlock = block === MOBILE_BLOCKS - 1;

  return (
    <Modal
      title={t('kycIntro.title')}
      onClose={onClose}
      closeAriaLabel={t('common.close')}
      size="lg"
      fullscreenBelow="sm"
      dialogClassName="objection-dialog"
      headerClassName="objection-header"
      renderHeader={titleId => (
        <div className="objection-header-main">
          <div className="objection-kicker">
            {t('kycIntro.kicker')}
            <span className="d-sm-none"> · {t('kycIntro.mobileStep', { current: block + 1, total: MOBILE_BLOCKS })}</span>
          </div>
          <div className="objection-header-title">
            <h2 className="modal-title" id={titleId}>{t('kycIntro.title')}</h2>
          </div>
        </div>
      )}
      footer={
        <div className="modal-action-row">
          {/* Phone: Nästa/Tillbaka through the blocks, then Fortsätt. */}
          {!lastBlock && (
            <button type="button" className="btn btn-primary d-sm-none" onClick={() => setBlock(block + 1)}>
              {t('kycIntro.actions.next')}
            </button>
          )}
          {block > 0 && (
            <button type="button" className="btn btn-outline-primary d-sm-none" onClick={() => setBlock(block - 1)}>
              {t('kycIntro.actions.back')}
            </button>
          )}
          <button type="button" className={`btn btn-outline-primary ${block > 0 ? 'd-none d-sm-inline-flex' : ''}`} onClick={onClose}>
            {t('kycIntro.actions.later')}
          </button>
          <button
            type="button"
            className={`btn btn-success ${lastBlock ? '' : 'd-none d-sm-inline-flex'}`}
            onClick={onContinue}
            data-testid="kyc-intro-continue"
          >
            {t('kycIntro.actions.continue')}
            <i className="feather-external-link ms-2" aria-hidden="true"></i>
          </button>
          <p className="modal-action-note text-explain mb-0 d-none d-sm-block">{t('kycIntro.newTab')}</p>
        </div>
      }
    >
      <div className="d-flex flex-column gap-4" data-testid="kyc-intro" data-variant="start">
        <div className={`${blockClass(0)}`}>
          <section className="objection-next is-action" aria-labelledby="kyc-intro-why">
            <span className="objection-next-icon" aria-hidden="true"><i className="feather-shield"></i></span>
            <div className="objection-next-body">
              <h3 id="kyc-intro-why" className="objection-next-title">{t('kycIntro.why.title')}</h3>
              <p className="mb-0">{t('kycIntro.why.text')}</p>
            </div>
          </section>
          <div className="mt-4">
            <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
              <h3 className="objection-section-title mb-0">{t('kycIntro.steps.title')}</h3>
              <span className="text-explain">
                <i className="feather-clock me-1" aria-hidden="true"></i>
                {t('kycIntro.steps.duration', { minutes: KYC_INTRO_MINUTES })}
              </span>
            </div>
            <CaseSteps steps={steps} currentStep={0} />
          </div>
        </div>

        <div className="row g-3">
          <div className={`col-12 col-md-6 ${blockClass(1)}`}>
            <section className="kyc-intro-prepare h-100" aria-labelledby="kyc-intro-prepare">
              <h3 id="kyc-intro-prepare" className="objection-section-title">{t('kycIntro.prepare.title')}</h3>
              <p className="text-explain d-sm-none">{t('kycIntro.prepare.intro')}</p>
              <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                {(['idDocument', 'exactName', 'representative', 'owners', 'bankAccount'] as const).map(item => (
                  <li key={item} className="d-flex gap-2">
                    <i className="feather-check kyc-intro-check" aria-hidden="true"></i>
                    <span>{t(`kycIntro.prepare.${item}`, { percent: KYC_INTRO_OWNER_PERCENT })}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          <div className={`col-12 col-md-6 ${blockClass(2)}`}>
            <section className="kyc-intro-requirement h-100" aria-labelledby="kyc-intro-requirement" data-testid="kyc-intro-requirement">
              <h3 id="kyc-intro-requirement" className="kyc-intro-requirement-title">
                <i className="feather-alert-triangle" aria-hidden="true"></i>
                {t('kycIntro.rightPerson.title')}
              </h3>
              <p>{t('kycIntro.rightPerson.who')}</p>
              <p>{t('kycIntro.rightPerson.consequence')}</p>
              <p className="mb-0">{t('kycIntro.rightPerson.more')}</p>
            </section>
            <p className="text-explain mt-3 mb-0 d-sm-none">{t('kycIntro.newTab')}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default KycIntroModal;
